import { Table } from '@tanstack/react-table';
import React from 'react';

import { IServerSideTableOptions } from '../../types';

import { ITablePaginationProps } from './table-pagination';

interface IUpdateServerPaginationParams {
  readonly isServerSide: boolean;
  readonly onPaginationChange: IServerSideTableOptions['onPaginationChange'];
  readonly newPageIndex: number;
  readonly currentPageSize: number;
}

const updateServerPagination = ({
  currentPageSize,
  newPageIndex,
  onPaginationChange,
}: IUpdateServerPaginationParams): void => {
  onPaginationChange({
    pageIndex: newPageIndex,
    pageSize: currentPageSize,
  });
};

function usePaginationState<TData>({ table, pageCount, manipulationOptions }: ITablePaginationProps<TData>) {
  const { pageIndex } = table.getState().pagination;
  const isServer = manipulationOptions?.type === 'serverSide';
  const currentPage = isServer ? manipulationOptions.currentPage : pageIndex;
  const canPrePage = isServer ? currentPage > 0 : table.getCanPreviousPage();
  const canNextPage = isServer ? currentPage < pageCount - 1 : table.getCanNextPage();

  return {
    canNextPage,
    canPreviousPage: canPrePage,
    currentPage,
    isServer,
  };
}
interface INavigationHandlersParams<TData> {
  readonly canNextPage: boolean;
  readonly canPreviousPage: boolean;
  readonly currentPageSize: number;
  readonly isServer: boolean;
  readonly manipulationOptions?: IServerSideTableOptions;
  readonly table: Table<TData>;
}

function useNavigationHandlers<TData>({
  canNextPage,
  canPreviousPage,
  currentPageSize,
  manipulationOptions,
  table,
}: INavigationHandlersParams<TData>) {
  const isServerSide = manipulationOptions?.type === 'serverSide';
  const handlePreviousPage = React.useCallback(() => {
    if (!canPreviousPage) return;
    table.previousPage();
    if (isServerSide)
      updateServerPagination({
        isServerSide: isServerSide,
        onPaginationChange: manipulationOptions?.onPaginationChange,
        newPageIndex: manipulationOptions.currentPage - 1,
        currentPageSize,
      });
  }, [table, isServerSide, canPreviousPage, manipulationOptions, currentPageSize]);
  const handleNextPage = React.useCallback(() => {
    if (!canNextPage) return;
    table.nextPage();
    if (isServerSide)
      updateServerPagination({
        isServerSide: isServerSide,
        onPaginationChange: manipulationOptions?.onPaginationChange,
        newPageIndex: manipulationOptions.currentPage + 1,
        currentPageSize,
      });
  }, [table, isServerSide, canNextPage, manipulationOptions, currentPageSize]);

  return {
    handlePreviousPage,
    handleNextPage,
  };
}
interface IGetHandlePageChangeParams {
  readonly pageCount: number;
  readonly isServer: boolean;
  readonly setPageIndex: (index: number) => void;
  readonly onPaginationChange?: IServerSideTableOptions['onPaginationChange'];
  readonly currentPageSize: number;
  readonly serverPaginationChange: typeof updateServerPagination;
}

const getHandlePageChange = ({
  pageCount,
  isServer,
  setPageIndex,
  onPaginationChange,
  currentPageSize,
  serverPaginationChange,
}: IGetHandlePageChangeParams) => {
  const handlePageChange = (value: string) => {
    const newPageIndex = Number(value);
    if (newPageIndex >= 0 && newPageIndex < pageCount) {
      setPageIndex(newPageIndex);
      if (isServer && onPaginationChange)
        serverPaginationChange({
          currentPageSize,
          isServerSide: isServer,
          newPageIndex,
          onPaginationChange,
        });
    }
  };

  return handlePageChange;
};

function usePaginationHandlers<TData>({
  table,
  currentPageSize,
  pageCount,
  manipulationOptions,
}: ITablePaginationProps<TData>) {
  const { isServer, currentPage, canPreviousPage, canNextPage } = usePaginationState({
    currentPageSize,
    manipulationOptions,
    pageCount,
    table,
  });

  const { handlePreviousPage, handleNextPage } = useNavigationHandlers({
    canNextPage,
    canPreviousPage,
    currentPageSize,
    isServer,
    manipulationOptions,
    table,
  });

  const handlePageChange = getHandlePageChange({
    currentPageSize,
    isServer,
    onPaginationChange: manipulationOptions?.onPaginationChange,
    pageCount,
    serverPaginationChange: updateServerPagination,
    setPageIndex: table.setPageIndex,
  });

  return {
    canNextPage,
    canPreviousPage,
    currentPage,
    handleNextPage,
    handlePageChange,
    handlePreviousPage,
  };
}

export { getHandlePageChange, updateServerPagination, useNavigationHandlers, usePaginationHandlers };

export type { INavigationHandlersParams };
